/**
 * ScrollableTabsList
 *
 * Drop-in replacement for `<Tabs.List>` that adds a horizontal-scroll affordance
 * on narrow screens. Mantine's default `Tabs.List` overflow is `hidden`, so on a
 * phone any tabs past the viewport edge are silently clipped and unreachable —
 * this wraps the list in a `ScrollArea` (horizontal only, scrollbar shown on
 * demand) and forces the tabs onto a single non-wrapping row.
 *
 * `type="scroll"`, NOT `"auto"`. Despite the name, Mantine's ScrollArea
 * `type="auto"` does not behave like CSS `overflow: auto` — its own source
 * (`ScrollAreaScrollbarAuto`) renders the SAME always-visible scrollbar
 * `type="always"` uses, permanently, whenever the content overflows, with no
 * fade or hide. That produced a thick gray bar sitting under every tab bar in
 * the app wherever the tabs overflow (reported live from an 11" iPad, but the
 * behavior is not device-specific — it is deterministic on every platform
 * once content overflows). `type="scroll"` (`ScrollAreaScrollbarScroll`) is
 * the one that starts hidden and shows only while actively scrolling, fading
 * out after `scrollHideDelay` — the "on demand" behavior this comment already
 * described before the wrong value was chosen.
 *
 * Must be rendered inside a `<Tabs>` ancestor — `Tabs.Tab` reads tab state from
 * React context, not DOM hierarchy, so the intervening ScrollArea is harmless.
 *
 * All props forward to the underlying `Tabs.List` (e.g. `grow`, `mb`, `style`).
 */

import { ScrollArea, Tabs } from "@mantine/core";
import type { TabsListProps } from "@mantine/core";

export function ScrollableTabsList({
  children,
  style,
  ...props
}: TabsListProps) {
  return (
    <ScrollArea type="scroll" scrollbars="x">
      <Tabs.List style={{ flexWrap: "nowrap", ...style }} {...props}>
        {children}
      </Tabs.List>
    </ScrollArea>
  );
}
